import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  PrimaryButton,
  SecondaryButton,
} from "../UI/DesignStandards";
import {
  defaultSignupSheetPages,
  entriesPerPage,
  sheetKindFromBowlerQuantity,
} from "../../utility/signupSheets";
import {
  DEFAULT_DOUBLES_SINGLES_SETUP,
  printDoublesFollowsSetupLabel,
  printDoublesSinglesMode,
  tournamentHasDoublesAndSingles,
} from "../../utility/doublesSinglesSetup";
import { fetchDoublesSinglesSetup } from "../../utility/doublesSinglesSetupApi";
import { downloadSignupSheetPdf } from "./downloadSignupSheetPdf";

const defaultPagesForEvent = (event) => {
  const kind = sheetKindFromBowlerQuantity(event?.bowlerQuantity);
  return String(
    defaultSignupSheetPages({
      entriesPerPage: entriesPerPage({ kind }),
    })
  );
};

const PrintSignupSheetDialog = ({
  isOpen,
  onClose,
  tournamentId,
  tournamentName,
  events = [],
}) => {
  const primaryEvents = useMemo(
    () => (events || []).filter((event) => event.isPrimary),
    [events]
  );
  const [selectedIds, setSelectedIds] = useState([]);
  const [pageCounts, setPageCounts] = useState({});
  const [doublesSinglesSetup, setDoublesSinglesSetup] = useState(
    DEFAULT_DOUBLES_SINGLES_SETUP
  );
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    setSelectedIds(primaryEvents.map((event) => event.id));
    const nextCounts = {};
    primaryEvents.forEach((event) => {
      nextCounts[event.id] = defaultPagesForEvent(event);
    });
    setPageCounts(nextCounts);
    let cancelled = false;
    fetchDoublesSinglesSetup(tournamentId)
      .then((next) => {
        if (!cancelled) setDoublesSinglesSetup(next);
      })
      .catch(() => {
        if (!cancelled) setDoublesSinglesSetup(DEFAULT_DOUBLES_SINGLES_SETUP);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, primaryEvents, tournamentId]);

  const selectedEvents = primaryEvents.filter((event) =>
    selectedIds.includes(event.id)
  );
  const showDoublesSingles = selectedEvents.some(
    (event) => Number(event.bowlerQuantity) === 2
  );
  const hasSinglesEvent = primaryEvents.some(
    (event) => Number(event.bowlerQuantity) === 1
  );
  const showDoublesFollowsSetup =
    showDoublesSingles && tournamentHasDoublesAndSingles(primaryEvents);

  const toggleEvent = (eventId) => {
    setSelectedIds((current) =>
      current.includes(eventId)
        ? current.filter((id) => id !== eventId)
        : [...current, eventId]
    );
  };

  const handlePrint = async () => {
    if (selectedEvents.length === 0) {
      setError("Choose at least one event.");
      return;
    }
    const counts = {};
    for (const event of selectedEvents) {
      const pages = parseInt(pageCounts[event.id], 10);
      if (!pages || pages < 1 || pages > 20) {
        setError("Enter a page count from 1 to 20 for each selected event.");
        return;
      }
      counts[event.id] = pages;
    }

    try {
      setPrinting(true);
      setError("");
      await downloadSignupSheetPdf({
        tournamentId,
        eventIds: selectedEvents.map((event) => event.id),
        eventName: selectedEvents[0]?.eventName,
        tournamentName,
        pageCounts: counts,
        doublesSingles: showDoublesSingles
          ? printDoublesSinglesMode(doublesSinglesSetup)
          : undefined,
      });
      onClose();
    } catch (printError) {
      setError(printError.message || "Could not create the PDF.");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Print signups"
      subtitle="Day-of signup sheets. Write the squad on the sheet. Choose the squad when you Import signups."
      maxWidth="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <SecondaryButton onClick={onClose} disabled={printing}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            onClick={handlePrint}
            loading={printing}
            disabled={printing || selectedEvents.length === 0}
          >
            Print
          </PrimaryButton>
        </div>
      }
    >
      <fieldset className="mb-4">
        <legend className="block text-sm font-medium text-brand-charcoal mb-2">
          Events
        </legend>
        {primaryEvents.length === 0 ? (
          <p className="text-sm text-brand-muted">No events</p>
        ) : (
          <div className="space-y-2">
            {primaryEvents.map((event) => {
              const checked = selectedIds.includes(event.id);
              return (
                <div
                  key={event.id}
                  className="flex items-center gap-3 text-sm"
                >
                  <label className="flex items-center gap-2 flex-1 min-w-0">
                    <input
                      type="checkbox"
                      className="shrink-0"
                      checked={checked}
                      onChange={() => toggleEvent(event.id)}
                    />
                    <span className="truncate">{event.eventName}</span>
                  </label>
                  <label className="flex items-center gap-2 shrink-0 text-brand-charcoal">
                    Pages
                    <input
                      type="number"
                      min={1}
                      max={20}
                      disabled={!checked}
                      value={pageCounts[event.id] || "2"}
                      onChange={(change) =>
                        setPageCounts((current) => ({
                          ...current,
                          [event.id]: change.target.value,
                        }))
                      }
                      className="w-16 px-2 py-1 border border-brand-charcoal/20 rounded-lg bg-white disabled:bg-gray-50"
                    />
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </fieldset>
      {showDoublesFollowsSetup ? (
        <p className="text-sm text-brand-charcoal mb-4">
          {printDoublesFollowsSetupLabel(doublesSinglesSetup)}
        </p>
      ) : null}
      <p className="text-sm text-brand-muted mb-2">
        Side pots, all event, and contact columns come from Setup. Squad is a
        write-in.
        {hasSinglesEvent
          ? " Check Singles if you also need a Singles signup sheet."
          : ""}
      </p>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
    </Modal>
  );
};

export default PrintSignupSheetDialog;
